import { QueryFactory } from '@lmt/shared-types';
import { CreatePasswordDto } from '@lmt/shared-types';
import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC } from 'react';
import { toast } from 'react-toastify';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useEventCallback } from '@mui/material';

type Form = CreatePasswordDto & {
  confirmPassword: string;
};

const nameof = nameofFactory<Form>();

type Props = {
  userId: string;
};

const containerStyle = {
  height: '16rem',
};
export const SetPassword: FC<FormComponentProps & Props> = (props) => {
  const { formId, tryCompleteForm } = props;
  const i18next = useScopedTranslation('users.change_password_popup');
  const form = useAdvancedForm<Form>(
    useEventCallback(async (data: Form) => {
      await tryCompleteForm('setPassword', formId, async () => {
        await QueryFactory.UserClient.changePasswordByAdmin(props.userId, {
          password: data.password,
        });
      });
      toast(i18next.t('set_password_successful_toast', { name: '' }), {
        type: 'success',
      });
    }),
    undefined,
    {
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );

  return (
    <form
      className={styles.container}
      style={containerStyle}
      onSubmit={form.handleSubmitDefault}
      data-test-id="setPasswordPopup"
    >
      <div className={styles.dataContainer}>
        <div className={styles.firstRow}>
          <Field
            className={styles.field}
            title={i18next.t('password')}
            required={true}
          >
            <Input
              {...form.register(nameof('password'), requiredRule())}
              type="password"
              errorText={form.formState.errors.password?.message}
              autoFocus={true}
            />
          </Field>
        </div>
        <Field
          className={styles.field}
          title={i18next.t('confirm_password')}
          required={true}
        >
          <Input
            {...form.register(nameof('confirmPassword'), {
              ...requiredRule(),
              validate: (value: string) =>
                value === form.getValues('password') ||
                (i18next.t('passwords_dont_match') as string),
            })}
            type="password"
            errorText={form.formState.errors.confirmPassword?.message}
          />
        </Field>
      </div>

      <div className={styles.submitContainer}>
        <Error error={form.overallError} />
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={styles.submit}
          title={i18next.t('set_password_for_other_user_button')}
        />
      </div>
    </form>
  );
};
